import { twMerge } from '@/lib/classname/twMerge'
import type { Loadable } from '@/ui/skeleton/Loadable'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import type { SkinProps } from '@/ui/skin/Skin'
import { Skin } from '@/ui/skin/Skin'
import { type VariantProps, cva } from 'class-variance-authority'

const SHADOW_CLASS = 'shadow-card' as const

/** Active under a `Link` (`group/link`); inert otherwise. Rest border avoids hover layout shift. */
const CARD_LINK_GROUP_STATE_CLASSNAME = twMerge(
  'border-px border-solid border-clear transition-colors hd:border-1.5',
  'group-data-[hovered]/link:bg-transparent group-data-[hovered]/link:border-primary group-data-[hovered]/link:text-primary',
  'group-data-[pressed]/link:bg-transparent group-data-[pressed]/link:border-primary group-data-[pressed]/link:text-primary',
  'group-data-[focus-visible]/link:outline group-data-[focus-visible]/link:outline-2 group-data-[focus-visible]/link:outline-offset-2 group-data-[focus-visible]/link:outline-brand-1-700',
)

export const paddingVariants = {
  none: '',
  sm: 'p-1',
  md: 'p-2',
  lg: 'p-3',
}

export const radiusVariants = {
  xs: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
}

export const cardCva = cva('flex', {
  variants: {
    padding: paddingVariants,
    radius: radiusVariants,
  },
})

type CardVariantProps = VariantProps<typeof cardCva>

type ConditionalColor =
  | {
      skin?: SkinProps['tint']
      bgColor?: never
    }
  | {
      bgColor?: string
      skin?: never
    }

export type CardProps = UiComponentProps<{
  children?: React.ReactNode
  /** Applies card shadow token */
  shadow?: boolean
}> &
  CardVariantProps &
  ConditionalColor &
  Loadable

export function Card({
  radius = 'md',
  padding = 'md',
  children,
  className,
  skin,
  bgColor,
  isLoading = false,
  shadow = false,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: CardProps) {
  const classNames = twMerge(
    cardCva({ radius, padding }),
    CARD_LINK_GROUP_STATE_CLASSNAME,
    shadow && SHADOW_CLASS,
    className,
  )

  if (skin !== undefined || bgColor === undefined) {
    return (
      <Skin
        id={id}
        tint={skin ?? 'body'}
        className={classNames}
        aria-label={ariaLabel}
        data-testid={dataTestId ?? 'card'}
      >
        {isLoading ? <BaseSkeleton className="min-h-6 w-full" /> : children}
      </Skin>
    )
  }

  return (
    <div
      id={id}
      style={{ background: bgColor }}
      className={classNames}
      aria-label={ariaLabel}
      data-testid={dataTestId ?? 'card'}
    >
      {isLoading ? <BaseSkeleton className="min-h-6 w-full" /> : children}
    </div>
  )
}
